import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaCheckCircle,
  FaLeaf,
  FaBarcode,
  FaBlender,
  FaBoxOpen,
  FaShieldAlt,
  FaBreadSlice,
  FaCookie,
  FaIceCream,
  FaPepperHot,
  FaSeedling,
  FaMortarPestle,
  FaThermometerHalf,
  FaBaby,
  FaUtensils,
  FaTimes,
  FaDownload,
  FaSun,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";

const FruitPowdersFlours = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const products = [
    {
      name: t("fruitPowdersFlours.products.bananaPowder.name"),
      images: [
        "/images/products/banana-powder-pack.webp",
        "/images/products/banana-powder-texture.webp",
      ],
      image: "🍌",
      color: "from-yellow-200 to-yellow-400",
      appearance: t("fruitPowdersFlours.products.bananaPowder.appearance"),
      aroma: t("fruitPowdersFlours.products.bananaPowder.aroma"),
      flavour: t("fruitPowdersFlours.products.bananaPowder.flavour"),
      texture: t("fruitPowdersFlours.products.bananaPowder.texture"),
      ingredient: t("fruitPowdersFlours.products.bananaPowder.ingredient"),
      uses: t("fruitPowdersFlours.products.bananaPowder.uses", {
        returnObjects: true,
      }),
      dataSheet: "/images/DataSheet/banana-powder.jpg",
    },
    {
      name: t("fruitPowdersFlours.products.mangoPowder.name"),
      images: [
        "/images/products/mango-powder-amchur-pack.webp",
        "/images/products/mango-powder-amchur-closeup.webp",
      ],
      image: "🥭",
      color: "from-amber-200 to-orange-400",
      appearance: t("fruitPowdersFlours.products.mangoPowder.appearance"),
      aroma: t("fruitPowdersFlours.products.mangoPowder.aroma"),
      flavour: t("fruitPowdersFlours.products.mangoPowder.flavour"),
      texture: t("fruitPowdersFlours.products.mangoPowder.texture"),
      ingredient: t("fruitPowdersFlours.products.mangoPowder.ingredient"),
      uses: t("fruitPowdersFlours.products.mangoPowder.uses", {
        returnObjects: true,
      }),
      dataSheet: "/images/DataSheet/mango-powder-amchur.jpg",
    },
    {
      name: t("fruitPowdersFlours.products.pineapplePowder.name"),
      images: [
        "/images/products/pineapple-powder-pack.webp",
        "/images/products/pineapple-powder-texture.webp",
      ],
      image: "🍍",
      color: "from-yellow-300 to-amber-400",
      appearance: t("fruitPowdersFlours.products.pineapplePowder.appearance"),
      aroma: t("fruitPowdersFlours.products.pineapplePowder.aroma"),
      flavour: t("fruitPowdersFlours.products.pineapplePowder.flavour"),
      texture: t("fruitPowdersFlours.products.pineapplePowder.texture"),
      ingredient: t("fruitPowdersFlours.products.pineapplePowder.ingredient"),
      uses: t("fruitPowdersFlours.products.pineapplePowder.uses", {
        returnObjects: true,
      }),
      dataSheet: "/images/DataSheet/pineapple-powder.jpg",
    },
    {
      name: t("fruitPowdersFlours.products.jackfruitFlour.name"),
      images: [
        "/images/products/jackfruit-flour-pack.webp",
        "/images/products/jackfruit-flour-texture.webp",
      ],
      image: "🍈",
      color: "from-green-200 to-yellow-300",
      appearance: t("fruitPowdersFlours.products.jackfruitFlour.appearance"),
      aroma: t("fruitPowdersFlours.products.jackfruitFlour.aroma"),
      flavour: t("fruitPowdersFlours.products.jackfruitFlour.flavour"),
      texture: t("fruitPowdersFlours.products.jackfruitFlour.texture"),
      ingredient: t("fruitPowdersFlours.products.jackfruitFlour.ingredient"),
      uses: t("fruitPowdersFlours.products.jackfruitFlour.uses", {
        returnObjects: true,
      }),
      dataSheet: "/images/DataSheet/jackfruit-flour.jpg",
    },
    {
      name: t("fruitPowdersFlours.products.jackfruitSeedFlour.name"),
      images: [
        "/images/products/jackfruit-seed-flour-pack.webp",
        "/images/products/jackfruit-seed-flour-texture.webp",
      ],
      image: "🌰",
      color: "from-amber-300 to-brown-400",
      appearance: t(
        "fruitPowdersFlours.products.jackfruitSeedFlour.appearance"
      ),
      aroma: t("fruitPowdersFlours.products.jackfruitSeedFlour.aroma"),
      flavour: t("fruitPowdersFlours.products.jackfruitSeedFlour.flavour"),
      texture: t("fruitPowdersFlours.products.jackfruitSeedFlour.texture"),
      ingredient: t(
        "fruitPowdersFlours.products.jackfruitSeedFlour.ingredient"
      ),
      uses: t("fruitPowdersFlours.products.jackfruitSeedFlour.uses", {
        returnObjects: true,
      }),
      dataSheet: "/images/DataSheet/jackfruit-seed-flour.jpg",
    },
  ];

  // Reset image index when product changes
  useEffect(() => {
    setActiveImageIndex(0);
  }, [activeProduct]);

  // Navigate images
  const nextImage = () => {
    setActiveImageIndex((prev) =>
      prev === products[activeProduct].images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? products[activeProduct].images.length - 1 : prev - 1
    );
  };

  const DownloadModal = () => (
    <AnimatePresence>
      {showDownloadModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  {t("fruitPowdersFlours.downloadModal.title")}
                </h3>
                <p className="text-amber-100 text-sm">
                  {t("fruitPowdersFlours.downloadModal.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Product List - Scrollable */}
            <div className="p-6 space-y-3 overflow-y-auto flex-1">
              {products.map((product, index) => (
                <motion.a
                  key={index}
                  href={product.dataSheet}
                  download
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-gray-700 dark:to-gray-600 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4">
                    {/* Product Icon */}
                    <div
                      className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center text-3xl shadow-md`}
                    >
                      {product.image}
                    </div>

                    {/* Product Info */}
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                        {product.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t("fruitPowdersFlours.downloadModal.productInfo")}
                      </p>
                    </div>
                  </div>

                  {/* Download Icon */}
                  <div className="bg-amber-600 text-white rounded-full p-3 group-hover:bg-amber-700 group-hover:scale-110 transition-all duration-300 shadow-md">
                    <FaDownload className="text-xl" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const certifications = [
    {
      name: t("fruitPowdersFlours.certifications.brc.name"),
      image: "/images/certifications/BRC.webp",
      alt: t("fruitPowdersFlours.certifications.brc.name"),
      description: t("fruitPowdersFlours.certifications.brc.description"),
      icon: "🏆",
    },
    {
      name: t("fruitPowdersFlours.certifications.eu.name"),
      image: "/images/certifications/EU.webp",
      alt: t("fruitPowdersFlours.certifications.eu.name"),
      description: t("fruitPowdersFlours.certifications.eu.description"),
      icon: "🇪🇺",
    },
    {
      name: t("fruitPowdersFlours.certifications.usda.name"),
      image: "/images/certifications/USDA.webp",
      alt: t("fruitPowdersFlours.certifications.usda.name"),
      description: t("fruitPowdersFlours.certifications.usda.description"),
      icon: "🇺🇸",
    },
    {
      name: t("fruitPowdersFlours.certifications.jas.name"),
      image: "/images/certifications/JAS.webp",
      alt: t("fruitPowdersFlours.certifications.jas.name"),
      description: t("fruitPowdersFlours.certifications.jas.description"),
      icon: "🇯🇵",
    },
    {
      name: t("fruitPowdersFlours.certifications.naturland.name"),
      image: "/images/certifications/Naturland.webp",
      alt: t("fruitPowdersFlours.certifications.naturland.name"),
      description: t("fruitPowdersFlours.certifications.naturland.description"),
      icon: "🌱",
    },
    {
      name: t("fruitPowdersFlours.certifications.fairtrade.name"),
      image: "/images/certifications/FairTrade.webp",
      alt: t("fruitPowdersFlours.certifications.fairtrade.name"),
      description: t("fruitPowdersFlours.certifications.fairtrade.description"),
      icon: "🤝",
    },
    {
      name: t("fruitPowdersFlours.certifications.fda.name"),
      image: "/images/certifications/FDA.webp",
      alt: t("fruitPowdersFlours.certifications.fda.name"),
      description: t("fruitPowdersFlours.certifications.fda.description"),
      icon: "✅",
    },
  ];

  const commonFeatures = [
    {
      icon: <FaSeedling />,
      title: t("fruitPowdersFlours.features.singleIngredient.title"),
      desc: t("fruitPowdersFlours.features.singleIngredient.desc"),
    },
    {
      icon: <FaMortarPestle />,
      title: t("fruitPowdersFlours.features.gentleProcessing.title"),
      desc: t("fruitPowdersFlours.features.gentleProcessing.desc"),
    },
    {
      icon: <FaBoxOpen />,
      title: t("fruitPowdersFlours.features.flexiblePackaging.title"),
      desc: t("fruitPowdersFlours.features.flexiblePackaging.desc"),
    },
    {
      icon: <FaLeaf />,
      title: t("fruitPowdersFlours.features.cleanLabel.title"),
      desc: t("fruitPowdersFlours.features.cleanLabel.desc"),
    },
  ];

  const applications = [
    {
      icon: <FaBlender />,
      text: t("fruitPowdersFlours.applications.smoothies"),
    },
    {
      icon: <FaBreadSlice />,
      text: t("fruitPowdersFlours.applications.bakery"),
    },
    {
      icon: <FaCookie />,
      text: t("fruitPowdersFlours.applications.confectionery"),
    },
    {
      icon: <FaIceCream />,
      text: t("fruitPowdersFlours.applications.desserts"),
    },
    {
      icon: <FaPepperHot />,
      text: t("fruitPowdersFlours.applications.seasonings"),
    },
    { icon: <FaBaby />, text: t("fruitPowdersFlours.applications.babyFoods") },
  ];

  const faqs = t("fruitPowdersFlours.faqs", { returnObjects: true });

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Organic Fruit Powders & Flours | Pure Single-Ingredient Powders"
        description="Premium organic fruit powders and flours from Sri Lanka. 100% single ingredient, no additives. Banana, Mango (Amchur), Pineapple, Jackfruit & Jackfruit Seed flour. BRC certified, gluten-free options. 24-month shelf life."
        keywords="organic fruit powder, organic banana powder, organic mango powder, amchur powder, organic pineapple powder, jackfruit flour, jackfruit seed flour, gluten-free flour, single ingredient powder, clean label powder, organic fruit flour Sri Lanka, BRC certified fruit powder, USDA organic powder, EU organic powder, natural fruit powder, dehydrated fruit powder, fruit powder for smoothies, fruit powder for baking, organic baby food powder, natural food coloring powder, vegan fruit powder, traceable organic powder"
        image="https://worganaturals.com/images/products/banana-powder-pack.webp"
        url="/products/fruit-powders-flours"
        type="product.group"
        productSchema={true}
        category="Organic Food Ingredients"
        brand="Worga Naturals"
        availability="InStock"
        price="8.99"
        currency="USD"
        sku="FPF-VARIETY"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Product Group Schema with All Variants */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProductGroup",
            name: "Organic Fruit Powders & Flours",
            description:
              "Premium organic fruit powders and flours from Sri Lanka. 100% single ingredient, hygienically processed, gently dehydrated and milled. No added sugars, colors, flavoring, or preservatives.",
            brand: {
              "@type": "Brand",
              name: "Worga Naturals",
              logo: "https://worganaturals.com/images/logo.png",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+94-11-234-5678",
                contactType: "Customer Service",
                email: "info@worganaturals.com",
              },
            },
            image: [
              "https://worganaturals.com/images/products/banana-powder-pack.webp",
              "https://worganaturals.com/images/products/mango-powder-amchur-pack.webp",
              "https://worganaturals.com/images/products/pineapple-powder-pack.webp",
              "https://worganaturals.com/images/products/jackfruit-flour-pack.webp",
              "https://worganaturals.com/images/products/jackfruit-seed-flour-pack.webp",
            ],
            category: "Organic Food Ingredients",
            offers: {
              "@type": "AggregateOffer",
              availability: "https://schema.org/InStock",
              priceCurrency: "USD",
              lowPrice: "8.99",
              highPrice: "14.99",
              offerCount: "5",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "89",
              bestRating: "5",
              worstRating: "1",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Shelf Life",
                value: "24 months",
              },
              {
                "@type": "PropertyValue",
                name: "Pack Sizes",
                value: "1 kg, 2.5 kg, Custom sizes available",
              },
              {
                "@type": "PropertyValue",
                name: "Certifications",
                value:
                  "BRC, EU Organic, USDA Organic, JAS, Naturland, Fairtrade, FDA Registered",
              },
              {
                "@type": "PropertyValue",
                name: "Origin",
                value: "Sri Lanka",
              },
              {
                "@type": "PropertyValue",
                name: "Processing Method",
                value: "Gentle dehydration and milling",
              },
              {
                "@type": "PropertyValue",
                name: "Free From",
                value: "Added sugars, colors, flavoring, preservatives",
              },
            ],
            hasVariant: [
              {
                "@type": "Product",
                name: "Organic Banana Powder",
                description:
                  "100% organic banana powder. Pale white color with prominent banana aroma and typical banana flavor. Fine powdered texture. Ideal for smoothies, baby foods, bakery, and instant dessert mixes.",
                sku: "FPF-BAN-1KG",
                gtin13: "1234567890123",
                image:
                  "https://worganaturals.com/images/products/banana-powder-pack.webp",
                offers: {
                  "@type": "Offer",
                  price: "8.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/fruit-powders-flours#banana",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Banana 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Pale white colour powder",
                  },
                ],
              },
              {
                "@type": "Product",
                name: "Organic Mango Powder (Amchur)",
                description:
                  "100% organic unripe mango powder. Light beige to pale brown with tart, fruity mango notes. Tangy dried green-mango flavor. Perfect for spice blends, marinades, chutneys, and as a souring agent.",
                sku: "FPF-MAN-1KG",
                gtin13: "1234567890124",
                image:
                  "https://worganaturals.com/images/products/mango-powder-amchur-pack.webp",
                offers: {
                  "@type": "Offer",
                  price: "12.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/fruit-powders-flours#mango",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Unripe Mango 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Also Known As",
                    value: "Amchur, Dried Mango Powder",
                  },
                ],
              },
              {
                "@type": "Product",
                name: "Organic Pineapple Powder",
                description:
                  "100% organic pineapple powder. Light yellow with distinct pineapple aroma and bright, sweet-tart flavor. Free-flowing fine powder. Excellent for beverage premixes, confectionery, yogurt bases, and bakery.",
                sku: "FPF-PIN-1KG",
                gtin13: "1234567890125",
                image:
                  "https://worganaturals.com/images/products/pineapple-powder-pack.webp",
                offers: {
                  "@type": "Offer",
                  price: "11.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/fruit-powders-flours#pineapple",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Pineapple 100%",
                  },
                ],
              },
              {
                "@type": "Product",
                name: "Organic Jackfruit Flour",
                description:
                  "100% organic jackfruit flour. White powder with prominent jackfruit aroma and typical flavor. Naturally gluten-free. Perfect for gluten-free baking, batters, porridges, and nutrition blends.",
                sku: "FPF-JAC-1KG",
                gtin13: "1234567890126",
                image:
                  "https://worganaturals.com/images/products/jackfruit-flour-pack.webp",
                offers: {
                  "@type": "Offer",
                  price: "10.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/fruit-powders-flours#jackfruit",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Jackfruit 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Dietary",
                    value: "Gluten-Free, Vegan",
                  },
                ],
              },
              {
                "@type": "Product",
                name: "Organic Jackfruit Seed Flour",
                description:
                  "100% organic jackfruit seed flour. White powder with brown particles, prominent jackfruit seed aroma. High-fiber, naturally gluten-free. Ideal for gluten-free formulations, bakery blends, and as a thickener.",
                sku: "FPF-JAS-1KG",
                gtin13: "1234567890127",
                image:
                  "https://worganaturals.com/images/products/jackfruit-seed-flour-pack.webp",
                offers: {
                  "@type": "Offer",
                  price: "14.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/fruit-powders-flours#jackfruit-seed",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Ingredient",
                    value: "Organic Jackfruit Seeds 100%",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Dietary",
                    value: "High-Fiber, Gluten-Free, Vegan",
                  },
                ],
              },
            ],
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Are there any additives in your fruit powders and flours?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No—100% fruit or seed, nothing added. Our powders and flours contain no added sugars, colors, flavoring, or preservatives. They are pure, single-ingredient organic products.",
                },
              },
              {
                "@type": "Question",
                name: "Are your fruit flours gluten-free?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, jackfruit flour and jackfruit seed flour are naturally gluten-free. They are processed in facilities following strict allergen controls to prevent cross-contamination.",
                },
              },
              {
                "@type": "Question",
                name: "Can I get private label or custom grind sizes?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—custom grind size, blends, and branding are available. We offer flexible packaging options and can accommodate specific particle size requirements for your applications.",
                },
              },
              {
                "@type": "Question",
                name: "What is the shelf life of your fruit powders and flours?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "24 months under recommended storage conditions. Store sealed in a cool, dry place away from sunlight. After opening, reseal or transfer to an airtight container.",
                },
              },
              {
                "@type": "Question",
                name: "How are your fruit powders processed?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Each fruit or seed is hygienically processed, gently dehydrated to preserve nutritional integrity, and carefully milled to achieve fine, free-flowing powders. This gentle processing preserves natural aroma and flavor.",
                },
              },
              {
                "@type": "Question",
                name: "What certifications do your fruit powders have?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Our fruit powders and flours are certified by BRC (British Retail Consortium), EU Organic, USDA Organic, JAS (Japanese Agricultural Standards), Naturland, Fairtrade, and FDA registered. All products are batch traceable.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Products",
                item: "https://worganaturals.com/products",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Fruit Powders & Flours",
                item: "https://worganaturals.com/products/fruit-powders-flours",
              },
            ],
          })}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals",
            url: "https://worganaturals.com",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "Premium organic products from Sri Lanka. Certified organic fruit powders, flours, coconut water, and natural preserves.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+94-11-234-5678",
              contactType: "Customer Service",
              email: "info@worganaturals.com",
              availableLanguage: ["English", "Sinhala"],
            },
            sameAs: [
              "https://www.facebook.com/worganaturals",
              "https://www.instagram.com/worganaturals",
              "https://www.linkedin.com/company/worganaturals",
            ],
          })}
        </script>

        {/* Recipe/HowTo Schema for Usage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Use Organic Fruit Powders",
            description:
              "Simple guide to using organic fruit powders and flours in your recipes",
            image:
              "https://worganaturals.com/images/how-to-use-fruit-powder.webp",
            step: [
              {
                "@type": "HowToStep",
                name: "Storage",
                text: "Keep sealed and store in cool, dry place away from sunlight. After opening, reseal or transfer to an airtight container.",
              },
              {
                "@type": "HowToStep",
                name: "For Beverages",
                text: "Pre-disperse powder in warm water for lump-free mixing. Add to smoothies, juices, or protein shakes.",
              },
              {
                "@type": "HowToStep",
                name: "For Baking",
                text: "Substitute up to 25% of regular flour with fruit flour for added nutrition and flavor. Mix thoroughly with dry ingredients before adding liquids.",
              },
              {
                "@type": "HowToStep",
                name: "For Seasoning",
                text: "Use mango powder (amchur) as a souring agent in curries, marinades, and spice blends. Start with small amounts and adjust to taste.",
              },
            ],
          })}
        </script>

        {/* Video Schema (if you have product videos) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "Organic Fruit Powders & Flours - Product Overview",
            description:
              "Discover our range of premium organic fruit powders and flours from Sri Lanka",
            thumbnailUrl:
              "https://worganaturals.com/images/video-thumbnails/fruit-powders.webp",
            uploadDate: "2024-01-15T08:00:00+05:30",
            duration: "PT2M30S",
            contentUrl:
              "https://worganaturals.com/videos/fruit-powders-overview.mp4",
            embedUrl: "https://www.youtube.com/embed/YOUR_VIDEO_ID",
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Download Modal */}
        <DownloadModal />
        {/* Spacer for navbar */}
        <div className="h-24"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("fruitPowdersFlours.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("fruitPowdersFlours.hero.title.part1")}{" "}
              </span>
              <span className="text-amber-600 dark:text-amber-500">
                {t("fruitPowdersFlours.hero.title.part2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
              {t("fruitPowdersFlours.hero.description")}
            </p>

            {/* Traceability Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-full px-6 py-3 shadow-lg"
            >
              <FaBarcode className="text-amber-600 text-2xl" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("fruitPowdersFlours.hero.traceability")}
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* Common Features */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("fruitPowdersFlours.whyChoose.title")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {commonFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Applications Section */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                {t("fruitPowdersFlours.idealFor.title")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {applications.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/30 transition-all duration-300"
                  >
                    <div className="text-3xl mb-2 flex justify-center">
                      {item.icon}
                    </div>
                    <p className="text-sm font-semibold">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Product Family */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("fruitPowdersFlours.productFamily.title")}
            </h2>

            {/* Product Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {products.map((product, index) => (
                <button
                  key={index}
                  onClick={() => setActiveProduct(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeProduct === index
                      ? "bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                  }`}
                >
                  <span className="hidden lg:inline">{product.name}</span>
                  <span className="lg:hidden">
                    {product.image} {product.name.split(" ")[1]}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Product Card */}
            <motion.div
              key={activeProduct}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="grid md:grid-cols-2 gap-8">
                {/* Product Image Slider */}
                <div
                  className={`bg-gradient-to-br ${products[activeProduct].color} flex items-center justify-center p-12 md:p-16 relative overflow-hidden`}
                >
                  {/* Image Display */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeImageIndex}
                      initial={{ opacity: 0, scale: 0.9, x: 100 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.9, x: -100 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className="w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={products[activeProduct].images[activeImageIndex]}
                        alt={`${products[activeProduct].name} - ${t(
                          "fruitPowdersFlours.navigation.view"
                        )} ${activeImageIndex + 1}`}
                        className="max-w-full max-h-[500px] object-contain drop-shadow-2xl rounded-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = document.createElement("div");
                          fallback.className = "text-9xl";
                          fallback.textContent = products[activeProduct].image;
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>

                  {/* Navigation Arrows */}
                  {products[activeProduct].images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t(
                          "fruitPowdersFlours.navigation.previousImage"
                        )}
                      >
                        <FaChevronLeft className="text-amber-600 text-2xl" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t(
                          "fruitPowdersFlours.navigation.nextImage"
                        )}
                      >
                        <FaChevronRight className="text-amber-600 text-2xl" />
                      </button>
                    </>
                  )}

                  {/* Image Indicators */}
                  {products[activeProduct].images.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2 z-10">
                      {products[activeProduct].images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveImageIndex(index)}
                          className={`transition-all duration-300 rounded-full ${
                            activeImageIndex === index
                              ? "bg-white w-10 h-3"
                              : "bg-white/50 hover:bg-white/75 w-3 h-3"
                          }`}
                          aria-label={t(
                            "fruitPowdersFlours.navigation.goToImage",
                            {
                              number: index + 1,
                            }
                          )}
                        />
                      ))}
                    </div>
                  )}

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold z-10">
                    {t("fruitPowdersFlours.navigation.imageCounter", {
                      current: activeImageIndex + 1,
                      total: products[activeProduct].images.length,
                    })}
                  </div>

                  {/* Decorative sun rays */}
                  <div className="absolute inset-0 opacity-20 pointer-events-none">
                    <FaSun className="absolute top-4 right-4 text-6xl text-white animate-pulse" />
                  </div>
                </div>

                {/* Product Details */}
                <div className="p-8 md:p-12">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      {t("fruitPowdersFlours.productDetails.badge")}
                    </span>
                  </div>

                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    {products[activeProduct].name}
                  </h3>

                  {/* Sensory Profile */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("fruitPowdersFlours.productDetails.appearance")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].appearance}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("fruitPowdersFlours.productDetails.aroma")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].aroma}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("fruitPowdersFlours.productDetails.flavour")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].flavour}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("fruitPowdersFlours.productDetails.texture")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].texture}
                      </span>
                    </div>
                  </div>

                  {/* Ingredient */}
                  <div className="bg-amber-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                    <h4 className="font-bold text-amber-600 dark:text-amber-400 mb-2">
                      {t("fruitPowdersFlours.productDetails.ingredient")}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-lg">
                      {products[activeProduct].ingredient}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      <strong>
                        {t("fruitPowdersFlours.productDetails.freeFrom")}:
                      </strong>{" "}
                      {t("fruitPowdersFlours.productDetails.freeFromList")}
                    </p>
                  </div>

                  {/* Uses */}
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      {t("fruitPowdersFlours.productDetails.uses")}:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {products[activeProduct].uses.map((use, i) => (
                        <div
                          key={i}
                          className="flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-3"
                        >
                          <FaCheckCircle className="text-amber-600 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {use}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Common Specs */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t("fruitPowdersFlours.productDetails.specs.origin")}:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "fruitPowdersFlours.productDetails.specs.originValue"
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "fruitPowdersFlours.productDetails.specs.shelfLife"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "fruitPowdersFlours.productDetails.specs.shelfLifeValue"
                          )}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "fruitPowdersFlours.productDetails.specs.packaging"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "fruitPowdersFlours.productDetails.specs.packagingValue"
                          )}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "fruitPowdersFlours.productDetails.specs.packSizes"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "fruitPowdersFlours.productDetails.specs.packSizesValue"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Process Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white shadow-xl"
            >
              <div className="flex items-start space-x-4">
                <FaMortarPestle className="text-5xl flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    {t("fruitPowdersFlours.process.title")}
                  </h3>
                  <p className="text-lg leading-relaxed">
                    {t("fruitPowdersFlours.process.description")}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Certifications */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("fruitPowdersFlours.certifications.title")}
            </h2>

            <div className="flex flex-wrap justify-center items-center gap-6 max-w-5xl mx-auto">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-16 h-16 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-xl p-2 group-hover:bg-orange-100 dark:group-hover:bg-gray-600 transition-colors">
                      <img
                        src={cert.image}
                        alt={cert.alt}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className = "text-2xl";
                          fallback.textContent = cert.icon;
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center max-w-[80px]">
                      {cert.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality & Safety */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("fruitPowdersFlours.quality.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: <FaShieldAlt />,
                  title: t("fruitPowdersFlours.quality.haccp.title"),
                  desc: t("fruitPowdersFlours.quality.haccp.desc"),
                },
                {
                  icon: <FaBarcode />,
                  title: t("fruitPowdersFlours.quality.traceability.title"),
                  desc: t("fruitPowdersFlours.quality.traceability.desc"),
                },
                {
                  icon: <FaThermometerHalf />,
                  title: t("fruitPowdersFlours.quality.stability.title"),
                  desc: t("fruitPowdersFlours.quality.stability.desc"),
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Usage & Storage */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                {t("fruitPowdersFlours.usage.title")}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <FaUtensils />,
                    title: t("fruitPowdersFlours.usage.readyToUse.title"),
                    text: t("fruitPowdersFlours.usage.readyToUse.text"),
                  },
                  {
                    icon: <FaBoxOpen />,
                    title: t("fruitPowdersFlours.usage.afterOpening.title"),
                    text: t("fruitPowdersFlours.usage.afterOpening.text"),
                  },
                  {
                    icon: <FaBlender />,
                    title: t("fruitPowdersFlours.usage.mixingTip.title"),
                    text: t("fruitPowdersFlours.usage.mixingTip.text"),
                  },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("fruitPowdersFlours.faqsTitle")}
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-amber-50 dark:bg-gray-700 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() =>
                        setOpenFaq(openFaq === index ? null : index)
                      }
                      className="w-full text-left p-6 flex justify-between items-center hover:bg-amber-100 dark:hover:bg-gray-600 transition-all duration-300"
                    >
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white pr-4">
                        {faq.q}
                      </h3>
                      <motion.div
                        animate={{ rotate: openFaq === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FaCheckCircle className="text-amber-600 text-2xl flex-shrink-0" />
                      </motion.div>
                    </button>
                    <motion.div
                      initial={false}
                      animate={{ height: openFaq === index ? "auto" : 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-gray-700 dark:text-gray-300">
                        {faq.a}
                      </p>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Technical Specs Teaser */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl"
          >
            <div className="text-center">
              <FaDownload className="text-6xl mb-4 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("fruitPowdersFlours.technical.title")}
              </h2>
              <p className="text-lg mb-6 opacity-90">
                {t("fruitPowdersFlours.technical.description")}
              </p>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center space-x-2 mx-auto"
              >
                <FaDownload />
                <span>{t("fruitPowdersFlours.technical.downloadButton")}</span>
              </button>
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default FruitPowdersFlours;
